import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useSearchParams } from 'react-router-dom';

import {
  CheckoutEventNames,
  CheckoutEventsItem,
  CheckoutEventsTotals,
  CheckoutSettings,
  Paddle,
  PaddleEventData,
  initializePaddle
} from '@paddle/paddle-js';
import cs from 'classnames';
import getSymbolFromCurrency from 'currency-symbol-map';
import { getFunctions, httpsCallable } from 'firebase/functions';

import styles from './Payment.module.scss';

import {
  invalidParametersError,
  invalidPlanError,
  paymentLinkError
} from '../../config/errors.config';
import { APP_TITLE } from '../../constants/app';
import { TEAM_MEMBERS_INCLUDED_IN_TEAM } from '../../constants/payment';
import {
  PADDLE_CLIENT_TOKEN,
  PADDLE_ENTERPRISE_PRODUCT_ANNUAL,
  PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL,
  PADDLE_ENTERPRISE_PRODUCT_MONTHLY,
  PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL,
  PADDLE_PERSONAL_PRODUCT,
  PADDLE_PERSONAL_PRODUCT_ANNUAL,
  PADDLE_PERSONAL_PRODUCT_MONTHLY,
  PADDLE_SANDBOX,
  PADDLE_TEAM_PRODUCT,
  PADDLE_TEAM_PRODUCT_ANNUAL,
  PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL,
  PADDLE_TEAM_PRODUCT_MONTHLY,
  PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL
} from '../../constants/secrets';
import { app } from '../../firebase';
import { getService } from '../../services';
import { ServiceError } from '../../services/errors';
import { getPaddleProductPrices } from '../../services/paddle';
import { usePricingPlansStore } from '../../stores/usePricingPlansStore';
import { ErrorCard } from '../../stories/ErrorCard/ErrorCard';
import { PaymentCard } from '../../stories/PaymentCard/PaymentCard';
import { Item, PaymentParams } from '../../types/payment.types';
import { Plan, PricingBillingPeriod } from '../../types/pricing.types';
import { Owner } from '../../types/services.types';
import { getUrlParams } from '../../util/params';
import { paymentSchema } from '../../validations/payment';
import { pricingPlanSchema } from '../../validations/pricingPlans';

interface PaymentLinkToken {
  billing: string;
  host: string;
  org: string;
  owner: Owner;
  plan: string;
  service: string;
  userId: string;
  username?: string;
}

const PADDLE_UPDATE_EVENTS = [
  CheckoutEventNames.CHECKOUT_LOADED,
  CheckoutEventNames.CHECKOUT_ITEMS_UPDATED,
  CheckoutEventNames.CHECKOUT_DISCOUNT_APPLIED,
  CheckoutEventNames.CHECKOUT_DISCOUNT_REMOVED,
  CheckoutEventNames.CHECKOUT_CUSTOMER_CREATED
];

const PADDLE_SETTINGS: CheckoutSettings = {
  displayMode: 'inline',
  theme: 'light',
  locale: 'en',
  frameTarget: styles.checkoutContainer,
  frameInitialHeight: 450,
  frameStyle:
    'width: 100%; min-width: 350px; background-color: transparent; border: none;'
};

export default function Payment() {
  const [searchParams] = useSearchParams();
  const { params, setPaddleProducts, paddleProducts } = usePricingPlansStore();

  const [plan, setPlan] = useState<Plan>();
  const [billedAnnually, setBilledAnnually] = useState(true);
  const [paddle, setPaddle] = useState<Paddle>();
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [items, setItems] = useState<CheckoutEventsItem[]>([]);
  const [totals, setTotals] = useState<CheckoutEventsTotals>();
  const [currency, setCurrency] = useState<string>();

  // TODO: Use a loading state
  const [error, setError] = useState<ServiceError>();

  useEffect(() => {
    void setupPaddle();
  }, []);

  const setupPaddle = async () => {
    const queryParams = getUrlParams<PaymentParams>(searchParams);

    const queryParamsParsed = paymentSchema.safeParse(queryParams);
    const paramsParsed = pricingPlanSchema.safeParse(params);

    if (!queryParamsParsed.success) {
      setError(
        new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        )
      );

      return;
    } else if (
      !queryParamsParsed.data.token &&
      !queryParamsParsed.data._ptxn &&
      !paramsParsed.success
    ) {
      setError(
        new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        )
      );

      return;
    }

    const products = await getPaddleProductPrices();
    setPaddleProducts(products);

    if (queryParamsParsed.data?._ptxn) {
      checkoutFromTransactionLink(queryParamsParsed.data._ptxn);
    } else {
      checkoutFromPlanOrSharedLink(
        queryParamsParsed.data,
        paramsParsed.data?.pat === 'true'
      );
    }
  };

  const checkoutFromTransactionLink = async (transactionId: string) => {
    try {
      const paddleInstance = await initializePaddle({
        environment: PADDLE_SANDBOX ? 'sandbox' : 'production',
        token: PADDLE_CLIENT_TOKEN,
        eventCallback: (event: PaddleEventData) => {
          if (!event.name || !event.data) return;

          if (PADDLE_UPDATE_EVENTS.includes(event.name)) {
            setItems(event.data.items);
            setTotals(event.data.totals);
            setCurrency(event.data.currency_code);

            const item = event.data.items[0];
            setPlan(
              item.product.id === PADDLE_PERSONAL_PRODUCT
                ? Plan.Personal
                : item.product.id === PADDLE_TEAM_PRODUCT
                  ? Plan.Team
                  : Plan.Enterprise
            );
            setBilledAnnually(
              event.data.items[0].billing_cycle?.interval !== 'month'
            );
          } else if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
            setCheckoutComplete(true);
          }
        }
      });

      setPaddle(paddleInstance);

      paddleInstance?.Checkout.open({
        settings: PADDLE_SETTINGS,
        transactionId
      });
    } catch (err: any) {
      if (err instanceof ServiceError) setError(err);
      else
        setError(
          new ServiceError(
            'Something went wrong',
            `We encountered an error: ${err.message}`
          )
        );
    }
  };

  const checkoutFromPlanOrSharedLink = async (
    parsedParams?: PaymentParams,
    usingPatToken: boolean = false
  ) => {
    try {
      let decodedToken: PaymentLinkToken | undefined;
      let owner: Owner | undefined;
      let usingSharedLink = false;
      if (parsedParams?.token) {
        decodedToken = JSON.parse(atob(parsedParams.token)) as PaymentLinkToken;

        if (
          !decodedToken.billing ||
          !decodedToken.host ||
          !decodedToken.org ||
          !decodedToken.owner ||
          !decodedToken.plan ||
          !decodedToken.service
        ) {
          throw new ServiceError(
            paymentLinkError.title,
            paymentLinkError.message
          );
        }
        usingSharedLink = true;
        owner = decodedToken.owner;
      }

      const plan = decodedToken?.plan ?? parsedParams?.plan;
      const billed = decodedToken?.billing ?? parsedParams?.billed;
      const userId = decodedToken?.userId ?? params?.user;
      const serviceName = decodedToken?.service ?? params?.service;
      const host = decodedToken?.host ?? params?.host;
      const org = decodedToken?.org ?? params?.org;

      const username = decodedToken?.username ?? params?.username ?? null;
      const token = !usingSharedLink && params?.token;

      if (
        !plan ||
        !billed ||
        !userId ||
        !serviceName ||
        !host ||
        !org ||
        (!usingSharedLink && !token)
      ) {
        throw new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        );
      }

      const billedAnnually = billed === PricingBillingPeriod.Annually;
      const service = getService(serviceName, host, usingPatToken, username);

      setPlan(plan as Plan);
      setBilledAnnually(billedAnnually);

      const pricingId = getPricingId(plan as Plan, billedAnnually);

      const paddleInstance = await initializePaddle({
        environment: PADDLE_SANDBOX ? 'sandbox' : 'production',
        token: PADDLE_CLIENT_TOKEN,
        eventCallback: (event: PaddleEventData) => {
          if (!event.name || !event.data) return;

          if (PADDLE_UPDATE_EVENTS.includes(event.name)) {
            setItems(event.data.items);
            setTotals(event.data.totals);
            setCurrency(event.data.currency_code);
          } else if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
            setCheckoutComplete(true);
          }
        }
      });

      if (!owner && token) {
        const organization = await service.getOrganization(token, org);
        owner = {
          id: organization.id,
          name: organization.displayName,
          type: organization.type,
          createdAt: organization.createdAt,
          enterprise: organization.enterprise
        };
      }

      if (owner?.enterprise && plan === Plan.Team) {
        throw new ServiceError(
          invalidPlanError.title,
          invalidPlanError.message
        );
      }

      // Get the number of members in the org
      const functions = getFunctions(app);
      const getNumberOfMembers = httpsCallable(functions, 'getNumberOfMembers');
      const response = await getNumberOfMembers({
        service: service.id,
        host,
        owner
      });
      const quantity = response.data as number;

      let customData: Record<string, string> = {
        userId,
        service: serviceName,
        host
      };
      const items: Item[] = [
        {
          priceId: pricingId,
          quantity: 1
        }
      ];

      if (plan === Plan.Team || plan === Plan.Enterprise) {
        customData = { ...customData, owner: JSON.stringify(owner) };

        const numAdditionalUsers = quantity - TEAM_MEMBERS_INCLUDED_IN_TEAM;
        if (numAdditionalUsers > 0) {
          items.push({
            priceId: getAdditionalPricingId(plan, billedAnnually),
            quantity: numAdditionalUsers
          });
        }
      }

      setPaddle(paddleInstance);

      paddleInstance?.Checkout.open({
        settings: PADDLE_SETTINGS,
        items,
        customData
      });
    } catch (err: any) {
      if (err instanceof ServiceError) setError(err);
      else
        setError(
          new ServiceError(
            'Something went wrong',
            `We encountered an error: ${err.message}`
          )
        );
    }
  };

  const getPricingId = (plan: Plan, billedAnnually: boolean): string => {
    if (plan === Plan.Personal && !billedAnnually) {
      return PADDLE_PERSONAL_PRODUCT_MONTHLY;
    } else if (plan === Plan.Personal && billedAnnually) {
      return PADDLE_PERSONAL_PRODUCT_ANNUAL;
    } else if (plan === Plan.Team && !billedAnnually) {
      return PADDLE_TEAM_PRODUCT_MONTHLY;
    } else if (plan === Plan.Team && billedAnnually) {
      return PADDLE_TEAM_PRODUCT_ANNUAL;
    } else if (plan == Plan.Enterprise && !billedAnnually) {
      return PADDLE_ENTERPRISE_PRODUCT_MONTHLY;
    } else if (plan == Plan.Enterprise && billedAnnually) {
      return PADDLE_ENTERPRISE_PRODUCT_ANNUAL;
    } else {
      throw new Error('Unknown plan');
    }
  };

  const getAdditionalPricingId = (
    plan: Plan,
    billedAnnually: boolean
  ): string => {
    if (plan === Plan.Team && !billedAnnually) {
      return PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL;
    } else if (plan === Plan.Team && billedAnnually) {
      return PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL;
    } else if (plan === Plan.Enterprise && !billedAnnually) {
      return PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL;
    } else if (plan === Plan.Enterprise && billedAnnually) {
      return PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL;
    } else {
      throw new Error('Unknown additional plan');
    }
  };

  const currencySymbol = getSymbolFromCurrency(currency || '');

  const toggleBillingPeriod = () => {
    setBilledAnnually(!billedAnnually);
    setItems([]);
    setTotals(undefined);

    if (paddle) {
      paddle?.Checkout.updateItems([
        {
          priceId: getPricingId(plan as Plan, !billedAnnually),
          quantity: 1
        }
      ]);
    }
  };

  if (error) {
    return (
      <section className={styles.container}>
        <Helmet>
          <title>{APP_TITLE} - Payment</title>
        </Helmet>
        <ErrorCard title={error.title} message={error.message} />
      </section>
    );
  }

  if (
    !paddleProducts ||
    !paddleProducts[Plan.Team] ||
    !paddleProducts[Plan.Enterprise]
  ) {
    return (
      <section className={styles.container}>
        <Helmet>
          <title>{APP_TITLE} - Payment</title>
        </Helmet>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      <Helmet>
        <title>{APP_TITLE} - Payment</title>
      </Helmet>
      <PaymentCard
        paddleLoaded={!!paddle}
        selectedPlan={plan}
        billed={
          billedAnnually
            ? PricingBillingPeriod.Annually
            : PricingBillingPeriod.Monthly
        }
        checkoutComplete={checkoutComplete}
        items={items}
        totals={totals}
        currencyCode={currency}
        currencySymbol={currencySymbol}
        products={paddleProducts}
        toggleBillingPeriod={toggleBillingPeriod}
      >
        <div
          className={cs({
            [styles.checkoutContainer]: true,
            [styles.hidePaddle]: !paddle,
            [styles.checkoutComplete]: checkoutComplete
          })}
        ></div>
      </PaymentCard>
    </section>
  );
}
